"use client"

import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { readLocalStorage, subscribeToMediaQuery, writeLocalStorage } from "@/lib/browser"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  resolvedTheme: "dark" | "light"
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  resolvedTheme: "dark",
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  storageKey = "drillpoint-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (readLocalStorage(storageKey) as Theme | null) || defaultTheme
  )

  const getResolvedTheme = (currentTheme: Theme): "dark" | "light" => {
    if (currentTheme === "system") {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return "dark"
      }
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
    }
    return currentTheme
  }

  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() =>
    getResolvedTheme(theme)
  )

  useEffect(() => {
    const root = window.document.documentElement
    const resolved = getResolvedTheme(theme)

    root.classList.remove("light", "dark")
    root.classList.add(resolved)
    setResolvedTheme(resolved)
  }, [theme])

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

    const handleChange = () => {
      if (theme === "system") {
        const resolved = getResolvedTheme(theme)
        const root = window.document.documentElement
        root.classList.remove("light", "dark")
        root.classList.add(resolved)
        setResolvedTheme(resolved)
      }
    }

    return subscribeToMediaQuery(mediaQuery, handleChange)
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      writeLocalStorage(storageKey, theme)
      setTheme(theme)
    },
    resolvedTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
